import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { usePortraitsTranslator } from "../i18n/translator";
import { FramedImage, PortraitFrame, SinglePortrait } from "./PortraitFrame";
import { ToggleLabel } from "./ToggleLabel";

type Face = "front" | "hover";

const FLIP_INTERVAL_MILLISECONDS = 3_000;

/**
 * Unattended, it shows both faces in turn, so nobody has to discover that
 * hovering over the card swaps them
 */
export const CardPreview = ({
  photoUrl,
  christmasImageUrl,
  autoFlip,
  startOnChristmasImage = false,
}: {
  photoUrl: string | undefined;
  christmasImageUrl: string | undefined;
  autoFlip: boolean;
  startOnChristmasImage?: boolean;
}) => {
  const translator = usePortraitsTranslator();
  const [held, setHeld] = useState<Face>();
  const [flipped, setFlipped] = useState<Face>(
    startOnChristmasImage ? "hover" : "front",
  );
  const [hovering, setHovering] = useState(false);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const cycling = autoFlip && !reducedMotion && !held && !hovering;

  useEffect(() => {
    if (!cycling) {
      return;
    }
    const timer = window.setInterval(
      () => setFlipped((face) => (face === "front" ? "hover" : "front")),
      FLIP_INTERVAL_MILLISECONDS,
    );
    return () => window.clearInterval(timer);
  }, [cycling]);

  if (!photoUrl || !christmasImageUrl) {
    return (
      <SinglePortrait
        imageUrl={photoUrl}
        alt={translator("cutoutAlt")}
        emptyLabel={translator("noCutoutYet")}
      />
    );
  }

  const face = hovering ? "hover" : (held ?? flipped);

  return (
    <Stack spacing={1} alignItems="center" width="100%">
      <PortraitFrame
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <FramedImage
          url={photoUrl}
          alt={translator("cutoutAlt")}
          shown={face === "front"}
        />
        <FramedImage
          url={christmasImageUrl}
          alt={translator("christmasImageAlt")}
          shown={face === "hover"}
        />
      </PortraitFrame>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={face}
        aria-label={translator("cardFace")}
        // Clicking the face already shown reports null, which holds that face
        onChange={(_, chosen: Face | null) => setHeld(chosen ?? face)}
        sx={{ width: 300, maxWidth: "100%" }}
      >
        <ToggleButton value="front" sx={{ flex: 1 }}>
          <ToggleLabel
            label={translator("cardFront")}
            caption={translator("cardFrontCaption")}
          />
        </ToggleButton>
        <ToggleButton value="hover" sx={{ flex: 1 }}>
          <ToggleLabel
            label={translator("onHover")}
            caption={translator("onHoverCaption")}
          />
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};
